import Seo from "../../components/seo";
import Layout from "../../components/layout";
import Header from "../../components/header";
import Footer from "../../components/footer";
import ServiceHero from "../../components/ServiceHero";
import ServiceCTA from "../../components/ServiceCTA";
import { CardColumn, HorizontalScroll } from "../../components/sections";
import { FiDatabase, FiBarChart2, FiTrendingUp, FiLayout, FiZap, FiCheckCircle, FiAlertCircle, FiGitBranch } from 'react-icons/fi';
import { usePageMetadata } from "../../hooks/usePageMetadata";

const BusinessIntelligencePage = () => {
  usePageMetadata({
    title: 'Business Intelligence - Bercastle',
    description: 'Data integration, ETL workflows, and business intelligence solutions for data-driven decision making.',
    pageType: 'service',
    content: {
      service: {
        name: "Business Intelligence",
        tagline: "Turn Data into Actionable Insights",
        description: "Comprehensive business intelligence solutions including data integration, ETL workflows, and interactive dashboards.",
        url: "/services/business-intelligence"
      },
      capabilities: [
        {
          name: "Data Integration & ETL",
          description: "Integrate data from multiple sources and streamline Extract, Transform, and Load workflows.",
          features: ["Multi-source data integration", "Real-time data pipelines", "Data quality assurance", "Automated ETL processes", "Data validation & cleansing", "Master data management"]
        },
        {
          name: "Interactive Dashboards",
          description: "Custom dashboards that provide real-time visibility into your key business metrics.",
          features: ["Real-time data visualization", "Drill-down capabilities", "Custom reports", "Mobile-responsive design", "Role-based access", "Scheduled reporting"]
        },
        {
          name: "Advanced Analytics",
          description: "Go beyond basic reporting with predictive analytics and statistical modeling.",
          solutions: ["Predictive modeling", "Trend analysis", "Statistical analysis", "What-if scenarios", "Forecasting", "Customer segmentation"]
        },
        {
          name: "Data Warehousing",
          description: "Design and implement scalable data warehouses for centralized data storage and analysis.",
          solutions: ["Dimensional modeling", "Star/snowflake schemas", "Data mart design", "Performance optimization", "Cloud data warehouses"]
        }
      ],
      platforms: ["Power BI", "Tableau", "Looker", "Qlik", "Custom Solutions"],
      data_sources: ["SQL databases", "NoSQL databases", "Cloud platforms", "CRM systems", "ERP systems", "Marketing platforms", "APIs and web services"],
      process: ["Requirements Gathering", "Data Assessment", "Architecture Design", "ETL Development", "Dashboard Creation", "Testing & Validation", "Training & Deployment", "Ongoing Support"],
      value_propositions: [
        "End-to-end BI expertise",
        "Business-focused insights",
        "Scalable solutions",
        "User-friendly dashboards",
        "Data security & governance",
        "Comprehensive training",
        "Ongoing support"
      ],
      meta: {
        source_url: "https://bercastle.com/services/business-intelligence",
        service_category: "Business Intelligence"
      }
    }
  });

  const biServices = [
    {
      icon: <FiDatabase size={24} />,
      title: 'Data Integration & ETL',
      description: 'Integrate data from multiple sources and streamline Extract, Transform, and Load workflows to ensure clean, consistent, and reliable data for your business.'
    },
    {
      icon: <FiBarChart2 size={24} />,
      title: 'Data Warehousing',
      description: 'Design and implement modern data warehouses to centralize your data, enabling efficient querying, reporting, and analysis at scale.'
    },
    {
      icon: <FiLayout size={24} />,
      title: 'Interactive Dashboards',
      description: 'Create intuitive, real-time dashboards that visualize key metrics and KPIs, making complex data easy to understand and act upon.'
    },
    {
      icon: <FiTrendingUp size={24} />,
      title: 'Advanced Analytics',
      description: 'Leverage predictive analytics, machine learning, and statistical models to forecast trends and uncover hidden patterns in your data.'
    },
    {
      icon: <FiZap size={24} />,
      title: 'Self-Service BI',
      description: 'Empower your teams with self-service tools that allow them to explore data, generate reports, and discover insights without IT dependency.'
    },
    {
      icon: <FiCheckCircle size={24} />,
      title: 'Automated Reporting',
      description: 'Set up automated reporting systems that deliver timely insights to stakeholders, reducing manual effort and ensuring data accuracy.'
    }
  ];

  const benefits = [
    {
      icon: <FiTrendingUp size={24} />,
      title: 'Actionable Insights',
      description: 'Uncover hidden trends and patterns to make informed business decisions that drive growth and competitive advantage.'
    },
    {
      icon: <FiZap size={24} />,
      title: 'Improved Efficiency',
      description: 'Save time and resources with automated reporting and streamlined workflows that eliminate manual data processing.'
    },
    {
      icon: <FiBarChart2 size={24} />,
      title: 'Real-Time Agility',
      description: 'Adapt quickly to market changes with real-time data at your fingertips, enabling faster response to opportunities.'
    },
    {
      icon: <FiGitBranch size={24} />,
      title: 'Scalable Solutions',
      description: 'BI systems grow with your business, accommodating new data sources and increased complexity seamlessly.'
    }
  ];

  const challenges = [
    {
      icon: <FiAlertCircle size={24} />,
      title: 'Disorganized Data',
      description: 'Consolidate scattered data into a centralized, actionable system'
    },
    {
      icon: <FiAlertCircle size={24} />,
      title: 'Lack of Insights',
      description: 'Provide real-time analytics and deep insights for informed decisions'
    },
    {
      icon: <FiAlertCircle size={24} />,
      title: 'Slow Reporting',
      description: 'Automate reporting workflows for faster, accurate insights'
    },
    {
      icon: <FiAlertCircle size={24} />,
      title: 'Siloed Data Systems',
      description: 'Integrate platforms for a 360-degree view of business performance'
    },
    {
      icon: <FiAlertCircle size={24} />,
      title: 'Inaccessible Dashboards',
      description: 'Create intuitive, user-friendly interfaces for easy interpretation'
    },
    {
      icon: <FiAlertCircle size={24} />,
      title: 'Scaling Challenges',
      description: 'Implement scalable solutions that grow with your business needs'
    }
  ];
  return (
    <Layout classmain="business-intelligence-page">
      <Seo
        title='Business Intelligence - OnScript AI'
        metaDesciption='Transform your data into actionable insights with our Business Intelligence solutions. Optimize decision-making and enhance operational efficiency.'
      />
      <Header />

      <ServiceHero 
        badge="Business Intelligence"
        title="Turn Data Into"
        titleHighlight="Actionable Insights"
        description="Empower your business with BI solutions that optimize decision-making, enhance operational efficiency, and unlock valuable insights."
        ctaLink="/contact"
      />

      {/* Services Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="BI Services We Offer"
            description="Comprehensive BI services to help you leverage data for smarter decisions"
            cards={biServices}
            cols={3}
            className='w-full'
          />
        </div>
      </div>

      {/* Benefits Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <HorizontalScroll
            title="Why Invest in BI Services?"
            description="Modern BI practices empower businesses to harness their data's full potential"
            items={benefits}
            cardWidth={300}
          />
        </div>
      </div>

      {/* Challenges Section */}
      <div className='py-20'>
        <div className='container-custom'>
          <CardColumn
            title="Challenges We Solve"
            description="Common BI challenges and how we address them"
            cards={challenges}
            cols={3}
            className='w-full'
          />
        </div>
      </div>

      <ServiceCTA 
        title="Ready to Unlock Your Data's Potential?"
        description="Let's build a BI solution that transforms your data into a strategic asset for business growth."
        buttonText="Start Your BI Project"
      />

      <Footer />
    </Layout>
  )
}

export default BusinessIntelligencePage
